import { useTranslation } from 'react-i18next';
import { BaseHeaderTitle, BaseImage } from '~/components/common';
import styles from './JobDetail.module.scss';
import classNames from 'classnames/bind';
import { Box, Button, Tab, Tabs } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { handleGetJob } from '~/thunks/job/jobThunk';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { handleGetJobTypes } from '~/thunks/job-type/jobTypeThunk';
import { handleGetTags } from '~/thunks/tag/tagThunk';
import { handleGetSelectAccounts } from '~/thunks/account/accountThunk';
import BasicInfo from './components/BasicInfo/BasicInfo';
import File from './components/File/File';
import Date from './components/date/Date';
import ActivitiesNotes from './components/Notes/Notes';
import Financial from './components/financial/Financial';
import { handleApiError } from '~/utils/helper';
import { IJob } from '~/utils/interface/job';
import AddTaskDialog from '../components/EditTaskDialog';
import { taskActions } from '~/thunks/task/taskSlice';
import { DIALOG_MODE_EDIT } from '~/utils/constants/common';
import TaskList from './components/task/taskList';
import { handleGetTask } from '~/thunks/task/taskThunk';
import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';
import EstimateList from './components/estimate/EstimateList';
import Activities from './components/Activities/Activities';
import Contact from './components/contact/Contact';
import AreaScope from './components/areaScope/AreaScope';
import Schedule from './components/schedule/Schedule';
import { jobActions } from '~/thunks/job/jobSlice';
import dayjs from 'dayjs';
import { IMenuItem } from '~/utils/interface/common';
import SendEmail from './components/SendEmail/SendEmail';

const cx = classNames.bind(styles);

const JobDetail = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { id } = useParams();
  const permissions = usePermission();
  const { t } = useTranslation(['admin/job', 'admin/common']);
  const dispatch = useAppDispatch();
  const { hash } = useLocation();
  const navigate = useNavigate();
  //#endregion Declare Hook

  //#region Selector
  const { openAlertModalEdit, mode } = useAppSelector((state) => state.task);
  //#endregion Selector
  //#region Declare State
  const [job, setJob] = useState<IJob>();
  const [page, setPage] = useState<string>('basicinformation');
  const [menuItems, setMenuItems] = useState<Array<IMenuItem>>([]);
  const [refreshJob, setRefreshJob] = useState<boolean>(false);
  const [openSendMailDialog, setOpenSendMailDialog] = useState<boolean>(false);
  //#endregion Declare State
  //#region Implement Hook
  const getMenuItems = useCallback(() => {
    const items: Array<IMenuItem> = [
      { label: 'basicinformation', component: <>{job && <BasicInfo data={job} updateData={onUpdateJob} />}</> },
    ];

    permissions[PERMISSIONS.contact_view] &&
      items.push({ label: 'contacts', component: <>{id && <Contact id={id} />}</> });

    permissions[PERMISSIONS.projectNote_view] &&
      items.push({ label: 'notes', component: <>{id && <ActivitiesNotes id={id} />}</> });

    items.push({ label: 'dates', component: <>{job && <Date data={job} updateData={onUpdateJob} />}</> });

    permissions[PERMISSIONS.projectAreaScope_view] &&
      items.push({ label: 'areaScope', component: <>{job && <AreaScope data={job} updateData={onUpdateJob} />}</> });

    permissions[PERMISSIONS.projectEstimate_view] &&
      items.push({
        label: 'estimate',
        component: <>{id && <EstimateList id={id} data={job} updateData={onUpdateJob} />}</>,
      });
    permissions[PERMISSIONS.schedule_view] &&
      items.push({
        label: 'schedules',
        component: <>{id && <Schedule jobId={id} />}</>,
      });

    items.push({ label: 'tasks', component: <>{id && <TaskList id={id} onOpen={handleOpenEdit} />}</> });

    permissions[PERMISSIONS.invoice_view] &&
      items.push({ label: 'financial', component: <>{job && <Financial data={job} updateData={onUpdateJob} />}</> });

    permissions[PERMISSIONS.projectFile_view] &&
      items.push({ label: 'files', component: <>{id && <File data={job} id={id} />}</> });

    permissions[PERMISSIONS.projectActivity_view] &&
      items.push({ label: 'activities', component: <>{id && <Activities id={id} />}</> });

    setMenuItems(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, job, permissions]);

  useEffect(() => {
    dispatch(handleGetJobTypes());
    dispatch(handleGetTags());
    dispatch(handleGetSelectAccounts({}));
    dispatch(jobActions.setScheduleCurrentTime(dayjs().format('YYYY-MM-DD')));
  }, [dispatch]);

  useEffect(() => {
    if (id || (id && refreshJob)) {
      setRefreshJob(false);
      (async () => {
        try {
          const job = await dispatch(handleGetJob(id.toString())).unwrap();
          job.tagIds = job.tags.map((item) => item.id);
          job.assigneeIds = job.assignees.map((item) => item.id);
          job.jobTypeId = job.jobType.id;
          setJob(job);
        } catch (e) {
          handleApiError(e);
        }
      })();
    }
  }, [id, dispatch, refreshJob]);

  useEffect(() => {
    if (hash) setPage(hash.slice(1));
  }, [hash]);

  useEffect(() => {
    getMenuItems();
  }, [getMenuItems]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    navigate(`#${newValue}`);
  };

  const handleOpenEdit = async (id: string) => {
    try {
      await dispatch(handleGetTask(id)).unwrap();
      dispatch(taskActions.changeMode(DIALOG_MODE_EDIT));
      dispatch(taskActions.changeOpenModalEdit(true));
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleSetOpen = async (open: boolean, needRefreshData?: boolean) => {
    dispatch(taskActions.changeOpenModalEdit(open));
  };

  const onUpdateJob = () => {
    setRefreshJob(true);
  };

  const renderTabContent = () => {
    const item = menuItems.find((menuItem: IMenuItem) => {
      return page === menuItem.label;
    });

    return item?.component;
  };

  //#endregion Handle Function
  return (
    <>
      {job && (
        <Box className='w-full h-full'>
          <Box className='flex'>
            <BaseImage src={job.logo} className='w-[38px] h-[38px] mr-[10px]' />
            <BaseHeaderTitle className='mb-[20px]' title={job?.name || t('title')} />

            {permissions[PERMISSIONS.send_email] && (
              <Box className='ml-auto'>
                <Button variant='contained' onClick={() => setOpenSendMailDialog(true)}>
                  {t('sendMail')}
                </Button>
              </Box>
            )}
          </Box>

          <Box className='mt-[20px] flex flex-col min-[1080px]:flex-row item-center mb-[50px] abc'>
            <Box className={cx('tab-list-container')}>
              <Tabs value={page} onChange={handleChange} variant='scrollable'>
                {menuItems.map((item: IMenuItem, index: number) => (
                  <Tab
                    value={item.label}
                    key={index}
                    label={t(`jobHeader.${item.label}`)}
                    className={cx('tab-item-container')}
                  />
                ))}
              </Tabs>
            </Box>
          </Box>
          <AddTaskDialog open={openAlertModalEdit} setOpen={handleSetOpen} mode={mode} data={job.id} job={job} />
          {renderTabContent()}
          <SendEmail open={openSendMailDialog} setOpen={(open) => setOpenSendMailDialog(open)} id={id} data={job} />
        </Box>
      )}
    </>
  );
};

export default JobDetail;
